import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type { Project } from '@/types';

/**
 * Hook to fetch paginated list of projects
 */
export function useProjects(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: queryKeys.projectList(params),
    queryFn: () => api.getProjects(params),
  });
}

/**
 * Hook to fetch a single project by ID
 */
export function useProject(id: string) {
  return useQuery({
    queryKey: queryKeys.projectDetail(id),
    queryFn: () => api.getProject(id),
    enabled: Boolean(id),
  });
}

/**
 * Hook to fetch workflows for a specific project
 */
export function useProjectWorkflows(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectWorkflows(projectId),
    queryFn: () => api.getProjectWorkflows(projectId),
    enabled: Boolean(projectId),
  });
}

/**
 * Hook to create a new project
 */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'stats'>) =>
      api.createProject(data),
    onSuccess: () => {
      // Invalidate projects list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Hook to update an existing project
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) =>
      api.updateProject(id, data),
    onSuccess: (updatedProject) => {
      // Update the specific project in cache
      queryClient.setQueryData(
        queryKeys.projectDetail(updatedProject.id),
        updatedProject
      );
      // Invalidate projects list
      queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Hook to delete a project
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.projectDetail(deletedId) });
      // Invalidate projects list
      queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}
